import { useCallback, useEffect, useRef, useState } from 'react';

interface UseTimelineScrollProps {
    containerRef: React.RefObject<HTMLDivElement>;
    currentTime: number;
    pixelsPerSecond: number;
    isCollapsed: boolean;
    audioFile?: File;
    isWaveformLoading?: boolean;
    onWheel?: (e: WheelEvent) => void;
    isDraggingSeekbar?: boolean;
}

export function useTimelineScroll({
    containerRef,
    currentTime,
    pixelsPerSecond,
    isCollapsed,
    audioFile,
    isWaveformLoading,
    onWheel,
    isDraggingSeekbar
}: UseTimelineScrollProps) {
    const [scrollLeft, setScrollLeft] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const isManualScrollingRef = useRef(false);
    const manualScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isProgrammaticScrollRef = useRef(false);

    useEffect(() => {
        if (!isCollapsed && containerRef.current && currentTime > 0) {
            const currentPosition = currentTime * pixelsPerSecond;
            const containerWidth = containerRef.current.clientWidth;
            const currentScrollLeft = containerRef.current.scrollLeft;

            const seekbarViewportPosition = currentPosition - currentScrollLeft;

            const scrollTriggerZone = containerWidth * 0.99;
            const repositionPoint = 0;

            let newScrollLeft = currentScrollLeft;

            if (seekbarViewportPosition > scrollTriggerZone) {
                newScrollLeft = currentPosition - repositionPoint;
            } else if (seekbarViewportPosition < 0) {
                newScrollLeft = Math.max(0, currentPosition - repositionPoint);
            }

            if (Math.abs(newScrollLeft - currentScrollLeft) > 10 && !isManualScrollingRef.current && !isDraggingSeekbar) {
                isProgrammaticScrollRef.current = true;
                containerRef.current.scrollLeft = newScrollLeft;
            }
        }
    }, [currentTime, pixelsPerSecond, isCollapsed, containerRef, isDraggingSeekbar]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            setScrollLeft(container.scrollLeft);

            if (isProgrammaticScrollRef.current) {
                isProgrammaticScrollRef.current = false;
            } else {
                isManualScrollingRef.current = true;
                if (manualScrollTimeoutRef.current) {
                    clearTimeout(manualScrollTimeoutRef.current);
                }
                manualScrollTimeoutRef.current = setTimeout(() => {
                    isManualScrollingRef.current = false;
                }, 2000);
            }
        };

        const handleResize = () => {
            setContainerWidth(container.clientWidth);
        };

        const handleWheel = (e: WheelEvent) => {
            onWheel?.(e);
        }

        setScrollLeft(container.scrollLeft);
        setContainerWidth(container.clientWidth);

        container.addEventListener('wheel', handleWheel, {passive: false});
        container.addEventListener('scroll', handleScroll, {passive: true});
        window.addEventListener('resize', handleResize);

        return () => {
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);

            if (manualScrollTimeoutRef.current) {
                clearTimeout(manualScrollTimeoutRef.current);
            }
        };
    }, [containerRef, onWheel, audioFile, isCollapsed]);

    return {
        scrollLeft,
        containerWidth,
    };
}
